import React, { useState, useEffect, useMemo, useCallback } from "react";
import useGuardedRealtimeList from '../../../realtime/useGuardedRealtimeList';
import PropTypes from "prop-types";
import { 
  Container, Row, Col, Card, Button, Form, Table, Accordion, 
  Modal, ButtonGroup, Spinner, Alert, Badge 
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectUserDetails } from "../../../redux/slices/userSlice";
import { selectDropdownOptions } from "../../../redux/slices/dropdownSlice";
import { selectUserDropdowns } from "../../../redux/slices/dropdownSlice";
import { fetchData, postData, deleteData } from "../../../redux/slices/apiCallSlice";
import axiosInstance from "../../../axios/axiosConfig";
import { formatDateForTable, formatDateForForm } from "../../../utils/dateUtils";
import useSendEmail from "../../../functions/useSendEmail";
import useUserContact from "../../../functions/useUserContact";
import { getUserEmailByRole } from "../../../functions/getUserEmailByRole";
import useActivityLogChangeLog from "../../../functions/useActivityLogChangeLog";
import "./AdvancedActivityLog.css";

const COMPACT_CONTROLS_MEDIA_QUERY =
  "(max-width: 767.98px), (min-width: 768px) and (max-width: 1366px) and (hover: none) and (pointer: coarse)";

const normalizeAccordionSelection = (selection) => {
  if (Array.isArray(selection)) return selection;
  return selection ? [selection] : [];
};

const getAccordionActiveKey = (keys, allowMultiple) => {
  if (allowMultiple) return keys;
  return keys?.[0] || null;
};

const AdvancedActivityLog = ({ showSaveNotificationMessage }) => {
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);
  const userDetails = useSelector(selectUserDetails);
  const currentUser = userDetails?.localAccountId;

  // Dropdown options
  const options = useSelector(selectDropdownOptions);
  const userDropdowns = useSelector(selectUserDropdowns);

  // API URLs
  const activityLogUrl = apiUrls.activityLog;
  const userPreferencesUrl = apiUrls.userPreferences;

  // Data state
  const [activityLog, setActivityLog] = useState([]);
  const [fullActivityLog, setFullActivityLog] = useState([]); // Store unfiltered data for search
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // View configuration state with default settings as specified
  const [viewType, setViewType] = useState("accordion"); // Default to accordion
  const [visibleColumns, setVisibleColumns] = useState([
    "status", "actions", "Market", "Status", "ProjectName", "JobNumber", "ProductCode", "RequestType", "RSM", "ProjectManager", 
    "Priority", "RequestDate", "DueDate", "DateCompleted", "Comments", "SupportEngineer", "NonEmployeeRequest", "Customer"
  ]);
  const [columnOrder, setColumnOrder] = useState([
    "status", "actions", "Market", "Status", "ProjectName", "JobNumber", "ProductCode", "RequestType", "RSM", "ProjectManager", 
    "Priority", "RequestDate", "DueDate", "DateCompleted", "Comments", "SupportEngineer", "NonEmployeeRequest", "Customer"
  ]);
  const [sortConfig, setSortConfig] = useState({ key: "DueDate", direction: "asc" }); // Sort by Due Date
  const [groupBy, setGroupBy] = useState("Market"); // Group by Market
  const [filters, setFilters] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [hideCompleted, setHideCompleted] = useState(true); // Hide completed by default
  const [showOnlyMyJobs, setShowOnlyMyJobs] = useState(true); // Default to show only user's jobs

  // Date completed range filter state
  const [dateCompletedRange, setDateCompletedRange] = useState({
    enabled: false,
    daysBack: 30
  });

  // Accordion preferences state
  const [keepAccordionsOpen, setKeepAccordionsOpen] = useState(false);
  const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
  const [nestedActiveKeys, setNestedActiveKeys] = useState({});
  const [controlsPanelOpen, setControlsPanelOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return !window.matchMedia(COMPACT_CONTROLS_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(COMPACT_CONTROLS_MEDIA_QUERY);
    const handleBreakpointChange = (event) => setControlsPanelOpen(!event.matches);

    mediaQuery.addEventListener("change", handleBreakpointChange);
    return () => mediaQuery.removeEventListener("change", handleBreakpointChange);
  }, []);

  // New record modal state
  const [showNewRecordModal, setShowNewRecordModal] = useState(false);
  const [newRecord, setNewRecord] = useState({
    Market: "",
    Status: "",
    ProjectName: "",
    JobNumber: "",
    ProductCode: "",
    RequestType: "",
    RSM: "",
    ProjectManager: userDetails?.displayName || userDetails?.name || "",
    Priority: "3", // Default to medium priority
    RequestDate: formatDateForForm(new Date()),
    DueDate: "",
    DateCompleted: "",
    Comments: "",
    SupportEngineer: "",
    NonEmployeeRequest: "",
    Customer: "",
    SendEmail: false
  });

  // Editable cell state
  const [editingCell, setEditingCell] = useState({ itemId: null, field: null });
  const [editValue, setEditValue] = useState("");
  const [originalValue, setOriginalValue] = useState("");

  // Email functionality hooks
  const sendEmail = useSendEmail();
  const { userContacts } = useUserContact();

  // Activity log change tracking hook
  const { logActivityLogChange } = useActivityLogChangeLog();

  // Helper function to show notifications via parent component
  const showNotification = (message, type = "info") => {
    showSaveNotificationMessage(message, type);
  };

  // Helper function for backwards compatibility with SaveBanner calls
  const setSaveStatus = (message) => {
    // Determine type based on message content
    const type = message.toLowerCase().includes("error") ? "error" : 
                 message.toLowerCase().includes("success") ? "success" : "info";
    showNotification(message, type);
  };

  const setShowSaveBanner = () => {
    // This function is now a no-op since notifications are handled by parent
  };

  // Status helper functions - enhanced to use proper date parsing
  const isOverdue = (dueDate, completedDate) => {
    if (completedDate) return false;
    if (!dueDate) return false;
    
    try {
      // Use formatDateUnified to properly parse dates and compare
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
      
      // Parse the due date using our date utilities logic
      let dueDateObj;
      if (typeof dueDate === 'string') {
        // Handle SQL datetime format: "2025-02-20 00:00:00.0000000"
        if (dueDate.includes(' ') && dueDate.includes(':')) {
          const datePart = dueDate.split(' ')[0];
          if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
            const [year, month, day] = datePart.split('-');
            dueDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          } else {
            dueDateObj = new Date(dueDate);
          }
        }
        // Handle ISO date format: "YYYY-MM-DD"
        else if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
          const [year, month, day] = dueDate.split('-');
          dueDateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
        // Handle other formats
        else {
          dueDateObj = new Date(dueDate);
        }
      } else {
        dueDateObj = new Date(dueDate);
      }
      
      if (isNaN(dueDateObj.getTime())) return false;
      
      return dueDateObj < today;
    } catch (error) {
      console.error('Error checking if date is overdue:', error);
      return false;
    }
  };

  // Modal states
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [showLoadViewModal, setShowLoadViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingRecord, setDeletingRecord] = useState(null);
  
  // Copy from existing states
  const [showCopySearch, setShowCopySearch] = useState(false);
  const [copySearchTerm, setCopySearchTerm] = useState("");
  const [copySearchResults, setCopySearchResults] = useState([]);
  
  // Saved views
  const [savedViews, setSavedViews] = useState([]);
  const [newViewName, setNewViewName] = useState("");
  const [newViewDescription, setNewViewDescription] = useState("");
  const [isLoadingView, setIsLoadingView] = useState(false); // Track when loading a view

  // Available columns configuration
  const availableColumns = [
    { key: "status", label: "", type: "status", width: "5px" },
    { key: "actions", label: "", type: "actions", width: "25px" },
    { key: "ID", label: "ID", type: "number" },
    { key: "Market", label: "Market", type: "text" },
    { key: "Status", label: "Status", type: "text" },
    { key: "ProjectName", label: "Project Name", type: "text" },
    { key: "JobNumber", label: "Job Number", type: "text" },
    { key: "ProductCode", label: "Product Code", type: "text" },
    { key: "RequestType", label: "Request Type", type: "text" },
    { key: "RSM", label: "RSM", type: "text" },
    { key: "ProjectManager", label: "Project Manager", type: "text" },
    { key: "Priority", label: "Priority", type: "select", options: [
      { value: "1", label: "1" },
      { value: "2", label: "2" },
      { value: "3", label: "3" },
      { value: "4", label: "4" },
      { value: "5", label: "5" }
    ]},
    { key: "RequestDate", label: "Request Date", type: "date" },
    { key: "DueDate", label: "Due Date", type: "date" },
    { key: "DateCompleted", label: "Date Completed", type: "date" },
    { key: "Comments", label: "Comments", type: "text" },
    { key: "SupportEngineer", label: "Support Engineer", type: "text" },
    { key: "NonEmployeeRequest", label: "Non-Employee Request", type: "text" },
    { key: "Customer", label: "Customer", type: "text" },
    { key: "SendEmail", label: "Send Email", type: "boolean" }
  ];

  // Grouping options
  const groupingOptions = [
    { value: "none", label: "No Grouping" },
    { value: "Market", label: "Market" },
    { value: "ProductCode", label: "Product Code" },
    { value: "RequestType", label: "Request Type" },
    { value: "ProjectManager", label: "Project Manager" },
    { value: "RSM", label: "RSM" },
    { value: "DateCompleted", label: "Date Completed" }
  ];

  // Dropdown options for editable fields
  const productCodeOptions = options
    ?.filter((option) => option.DropdownType === "Project Code")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];
    
  const requestTypeOptions = options
    ?.filter((option) => option.DropdownType === "Request Type")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  // Market options from dropdown slice - using Activity Log Market dropdown
  const marketOptions = options
    ?.filter((option) => option.DropdownType === "Activity Log Market")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  const activityLogStatusOptions = options
    ?.filter((option) => option.DropdownType === "Activity Log Status")
    .sort((a, b) => a.SortOrder - b.SortOrder) || [];

  // Market options for new record modal (use dropdown options first, fallback to unique values)
  const marketOptionsForModal = marketOptions.length > 0 
    ? marketOptions 
    : [...new Set(activityLog.map(item => item.Market).filter(Boolean))].sort().map(market => ({ DisplayText: market }));

  // RSM options (Sales Reps)
  const rsmOptions = userDropdowns?.salesOptions || [];
  
  // Project Manager options (Apps + Admin users)
  const projectManagerOptions = [
    ...(userDropdowns?.appsOptions || []),
    ...(userDropdowns?.adminOptions || []),
  ];

  // Support Engineer options (Engineering staff)
  const supportEngineerOptions = [
    ...(userDropdowns?.mechOptions?.map(option => ({ ...option, group: "Mechanical" })) || []),
    ...(userDropdowns?.elecOptions?.map(option => ({ ...option, group: "Electrical" })) || []),
    ...(userDropdowns?.controlOptions?.map(option => ({ ...option, group: "Controls" })) || []),
  ];

  // Fetch activity log data
  useGuardedRealtimeList({ apiKey: 'activityLog', apply: setFullActivityLog, blocked: loading || showEditModal || showDeleteConfirm });
  useGuardedRealtimeList({ apiKey: 'activityLog', params: hideCompleted ? { status: 'incomplete' } : undefined,
    apply: setActivityLog, blocked: loading || showEditModal || showDeleteConfirm });
  const fetchActivityLog = useCallback(async () => {
    if (!activityLogUrl) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Always fetch full data for search functionality
      const fullResponse = await dispatch(fetchData({ url: activityLogUrl })).unwrap();
      setFullActivityLog(fullResponse || []);
      
      // Add status filter based on hideCompleted setting for display
      const urlWithParams = hideCompleted 
        ? `${activityLogUrl}?status=incomplete`
        : activityLogUrl;
      
      const response = await dispatch(fetchData({ url: urlWithParams })).unwrap();
      setActivityLog(response || []);
    } catch (err) {
      console.error("Error fetching activity log:", err);
      setError("Failed to load activity log data");
    } finally {
      setLoading(false);
    }
  }, [dispatch, activityLogUrl, hideCompleted]);

  // Fetch saved views
  useGuardedRealtimeList({ apiKey: 'userPreferences', enabled: Boolean(currentUser),
    apply: rows => setSavedViews(rows.filter(row => (row.Category ?? row.category) === 'AdvancedActivityLogViews' && (row.UserObjectID ?? row.userObjectID) === currentUser)),
  });

  const fetchSavedViews = useCallback(async () => {
    if (!userPreferencesUrl || !currentUser) return;
    
    try {
      const response = await dispatch(fetchData({ url: userPreferencesUrl })).unwrap();
      const userViews = response.filter(pref => 
        pref.UserObjectID === currentUser && 
        pref.Category === "AdvancedActivityLogViews"
      );
      setSavedViews(userViews);
    } catch (err) {
      console.error("Error fetching saved views:", err);
    }
  }, [dispatch, userPreferencesUrl, currentUser]);

  // Initial data fetch
  useEffect(() => {
    fetchActivityLog();
    fetchSavedViews();
  }, [fetchActivityLog, fetchSavedViews]);

  // Refetch data when hideCompleted changes to apply/remove status filter
  useEffect(() => {
    fetchActivityLog();
  }, [hideCompleted, fetchActivityLog]);

  // Clear incompatible filters when "Show Only My Jobs" toggle changes
  useEffect(() => {
    // Only clear filters if we're not currently loading a saved view
    // This prevents clearing filters that were just loaded from a saved view
    if (!isLoadingView) {
      // When showOnlyMyJobs changes, clear existing filters that might not be valid
      // for the new data scope
      setFilters({});
    }
  }, [showOnlyMyJobs, isLoadingView]);

  // Clear DateCompleted filter when date range filter is enabled/disabled
  useEffect(() => {
    if (dateCompletedRange.enabled) {
      // Remove DateCompleted from regular filters as it's now handled by the range filter
      setFilters(prev => {
        const { DateCompleted, ...rest } = prev;
        return rest;
      });
    }
  }, [dateCompletedRange.enabled]);

  // Format date for display using unified date utilities
  const formatDate = (dateString) => {
    return formatDateForTable(dateString);
  };

  // Get filtered unique values based on current showOnlyMyJobs setting
  const getFilteredUniqueValues = (columnKey) => {
    // Use dropdown options for Market field instead of unique values from data
    if (columnKey === 'Market') {
      return marketOptions.map(option => option.DisplayText);
    }
    
    let baseData = activityLog;
    
    // If showing only my jobs, filter the base data first
    if (showOnlyMyJobs && (userDetails?.displayName || userDetails?.name)) {
      const userName = userDetails?.displayName || userDetails?.name;
      baseData = baseData.filter(item => item.ProjectManager === userName);
    }
    
    if (columnKey === 'Status') {
      return activityLogStatusOptions.map(option => option.DisplayText).filter(Boolean);
    }
    
    return [...new Set(baseData.map(item => item[columnKey]).filter(Boolean))].sort();
  };

  // Filter and search logic
  const filteredData = useMemo(() => {
    let filtered = activityLog;

    // Apply "show only my jobs" filter
    if (showOnlyMyJobs && (userDetails?.displayName || userDetails?.name)) {
      const userName = userDetails?.displayName || userDetails?.name;
      filtered = filtered.filter(item => item.ProjectManager === userName);
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(item =>
        Object.values(item).some(value =>
          value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        filtered = filtered.filter(item => item[key] === value);
      }
    });

    // Apply date completed range filter - show completed jobs from date range AND all uncompleted items
    if (dateCompletedRange.enabled) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - dateCompletedRange.daysBack);
      cutoffDate.setHours(0, 0, 0, 0); // Set to start of day
      
      filtered = filtered.filter(item => {
        // Show all uncompleted items
        if (!item.DateCompleted) return true;
        // Show completed items within the date range
        const completedDate = new Date(item.DateCompleted);
        return completedDate >= cutoffDate;
      });
    }

    // Hide completed items if hideCompleted is true
    if (hideCompleted) {
      filtered = filtered.filter(item => !item.DateCompleted);
    }

    return filtered;
  }, [activityLog, searchTerm, filters, hideCompleted, showOnlyMyJobs, dateCompletedRange, userDetails?.name, userDetails?.displayName]);

  // Sort logic
  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      // When date range filter is enabled, prioritize uncompleted items at the top
      if (dateCompletedRange.enabled) {
        const aCompleted = !!a.DateCompleted;
        const bCompleted = !!b.DateCompleted;
        
        // If completion status is different, uncompleted comes first
        if (aCompleted !== bCompleted) {
          return aCompleted ? 1 : -1; // uncompleted (false) comes before completed (true)
        }
      }
      
      // Apply regular sorting if no sort key is set
      if (!sortConfig.key) return 0;

      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key.includes("Date")) {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      }

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig, dateCompletedRange.enabled]);

  // Group logic with secondary grouping by ProjectName
  const groupedData = useMemo(() => {
    const groups = groupBy === "none" ? { "All Items": sortedData } : 
      sortedData.reduce((groups, item) => {
        let groupKey = item[groupBy] || "Unassigned";
        
        // Special handling for date fields to format them for better grouping
        if (groupBy === "DateCompleted") {
          if (groupKey && groupKey !== "Unassigned") {
            groupKey = formatDate(groupKey);
          } else {
            groupKey = "Not Completed";
          }
        } else if (groupBy.includes("Date") && groupKey && groupKey !== "Unassigned") {
          groupKey = formatDate(groupKey);
        }
        
        if (!groups[groupKey]) groups[groupKey] = [];
        groups[groupKey].push(item);
        return groups;
      }, {});

    // Apply secondary grouping by ProjectName within each primary group
    const nestedGroups = {};
    Object.entries(groups).forEach(([primaryGroupKey, items]) => {
      const secondaryGroups = items.reduce((subGroups, item) => {
        const projectName = item.ProjectName || "Unassigned Project";
        if (!subGroups[projectName]) subGroups[projectName] = [];
        subGroups[projectName].push(item);
        return subGroups;
      }, {});
      
      nestedGroups[primaryGroupKey] = secondaryGroups;
    });

    return nestedGroups;
  }, [sortedData, groupBy]);

  // Handle accordion open/close based on keepAccordionsOpen preference
  useEffect(() => {
    if (keepAccordionsOpen) {
      // Open all main accordions
      const mainKeys = Object.keys(groupedData).map((_, index) => index.toString());
      setActiveAccordionKeys(mainKeys);
      
      // Open all nested accordions for each group
      const nestedKeys = {};
      Object.entries(groupedData).forEach(([groupName, projectGroups], groupIndex) => {
        const projectKeys = Object.keys(projectGroups).map((_, index) => index.toString());
        nestedKeys[groupIndex.toString()] = projectKeys;
      });
      setNestedActiveKeys(nestedKeys);
    } else {
      // Close all accordions except first one
      setActiveAccordionKeys(['0']);
      setNestedActiveKeys({});
    }
  }, [keepAccordionsOpen, groupedData]);

  // Send completion email notification
  const sendCompletionEmail = async (item) => {
    try {
      let recipientEmail = '';
      let recipientName = '';

      // Check if RSM is set to "house" - if so, send to current user
      if (item.RSM && item.RSM.toLowerCase() === 'house') {
        recipientEmail = userDetails?.username; // username contains the email
        recipientName = userDetails?.displayName || userDetails?.name || 'User';
        
        if (!recipientEmail) {
          console.warn('No email found for current user when RSM is set to "house"');
          return;
        }
      } else {
        // Create a project details object with RSM as SalesRep
        const projectDetails = {
          SalesRep: item.RSM
        };

        // Use getUserEmailByRole to find the RSM (SalesRep) email
        const projectTeamMembers = getUserEmailByRole(userContacts, projectDetails);
        const rsmMember = projectTeamMembers.find(member => member.role === 'Sales Representative');
        
        if (!rsmMember || !rsmMember.email) {
          console.warn(`No email found for RSM: ${item.RSM}`);
          return;
        }

        recipientEmail = rsmMember.email;
        recipientName = rsmMember.displayName;
      }

      const subject = `${item.ProjectName} - ${item.RequestType} is Complete`;
      const body = `${recipientName},<br><br>Your ${item.RequestType} for ${item.ProjectName} has been completed.<br><br>Please contact ${item.ProjectManager} if you have any additional requests or questions.`;

      await sendEmail({
        subject: subject,
        body: body,
        toRecipients: [recipientEmail],
        from: "notify@watertronics.com"
      });

      console.log(`Completion email sent to ${recipientEmail} (${recipientName}) for project: ${item.ProjectName}`);
    } catch (error) {
      console.error('Failed to send completion email:', error);
    }
  };

  // Send Project Manager change notification email
  const sendProjectManagerChangeEmail = async (item, previousProjectManager, newProjectManager) => {
    try {
      // Only send notifications if there was a previous Project Manager (not blank)
      // If previous PM is blank, this is an initial assignment, not a reassignment
      if (!previousProjectManager || previousProjectManager.trim() === '') {
        console.log(`Skipping Project Manager change notification - initial assignment of ${newProjectManager} to ${item.ProjectName}`);
        return;
      }

      const recipients = [];
      const currentUserEmail = (userDetails?.username || userDetails?.email || '').toLowerCase(); // Current user's email
      const normalizedPrev = previousProjectManager?.trim();
      const normalizedNew = newProjectManager?.trim();
      // Helper to find a PM entry by displayName (case-insensitive)
      const findPm = (name) => {
        if (!name) return null;
        return projectManagerOptions.find(pm => (pm.displayName || '').toLowerCase() === name.toLowerCase());
      };
      
      // Find email for previous Project Manager
      if (normalizedPrev) {
        const prevPMDetails = findPm(normalizedPrev);
        if (prevPMDetails && prevPMDetails.email) {
          const prevEmail = prevPMDetails.email.toLowerCase();
            if (prevEmail !== currentUserEmail) {
              recipients.push({
                email: prevPMDetails.email,
                name: prevPMDetails.displayName || normalizedPrev
              });
            } else {
              console.log(`Skipping notification to previous Project Manager ${previousProjectManager} - they are the current user making the change`);
            }
        } else {
          console.warn(`No email found for previous Project Manager: ${previousProjectManager}`);
        }
      }

      // Find email for new Project Manager
      if (normalizedNew) {
        const newPMDetails = findPm(normalizedNew);
        if (newPMDetails && newPMDetails.email) {
          const newEmail = newPMDetails.email.toLowerCase();
            if (newEmail !== currentUserEmail) {
              recipients.push({
                email: newPMDetails.email,
                name: newPMDetails.displayName || normalizedNew
              });
            } else {
              console.log(`Skipping notification to new Project Manager ${newProjectManager} - they are the current user making the change`);
            }
        } else {
          console.warn(`No email found for new Project Manager: ${newProjectManager}`);
        }
      }

      // Send emails to recipients if we have any (excluding current user)
      if (recipients.length > 0) {
        const subject = `Activity Log Assignment Change - ${item.ProjectName}`;
        const body = `${item.RequestType} - ${item.ProjectName} has been reassigned from ${previousProjectManager} to ${newProjectManager}.`;

        const recipientEmails = recipients.map(r => r.email);

        await sendEmail({
          subject: subject,
          body: body,
          toRecipients: recipientEmails,
          from: "notify@watertronics.com"
        });

        console.log(`Project Manager change email sent to ${recipientEmails.join(', ')} for project: ${item.ProjectName}`);
      } else {
        console.log('No external recipients for Project Manager change notification - all affected users are the current user making the change');
      }
    } catch (error) {
      console.error('Failed to send Project Manager change email:', error);
    }
  };

  // Handle sorting
  const handleSort = (columnKey) => {
    setSortConfig(prev => ({
      key: columnKey,
      direction: prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  // Handle filter change
  const handleFilterChange = (columnKey, value) => {
    setFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
    setHideCompleted(true); // Reset to default (hide completed)
    setDateCompletedRange({ enabled: false, daysBack: 30 }); // Reset date range filter
    setKeepAccordionsOpen(false); // Reset accordion preference
    setActiveAccordionKeys(['0']); // Reset to first accordion open
    setNestedActiveKeys({}); // Reset nested accordion states
    // Don't change showOnlyMyJobs - that's a personal preference toggle
  };

  // Handle new record creation
  const handleAddNewRecord = () => {
    setNewRecord({
      Market: "",
      Status: "",
      ProjectName: "",
      JobNumber: "",
      ProductCode: "",
      RequestType: "",
      RSM: "",
      ProjectManager: userDetails?.displayName || userDetails?.name || "",
      RequestDate: formatDateForForm(new Date()),
      DueDate: "",
      Comments: "",
      SupportEngineer: "",
      NonEmployeeRequest: "",
      Customer: "",
      SendEmail: false
    });
    setShowNewRecordModal(true);
  };

  // Handle new record input changes
  const handleNewRecordChange = (field, value) => {
    setNewRecord(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save new record
  const handleSaveNewRecord = async () => {
    if (!activityLogUrl) return;

    try {
      // Map form data to API format using explicit PascalCase field names
      const apiData = {
        Market: newRecord.Market || null,
        Status: newRecord.Status || null,
        ProjectName: newRecord.ProjectName || null,
        JobNumber: newRecord.JobNumber || null,
        ProductCode: newRecord.ProductCode || null,
        RequestType: newRecord.RequestType || null,
        RSM: newRecord.RSM || null,
        ProjectManager: newRecord.ProjectManager || null,
        Priority: newRecord.Priority !== null && newRecord.Priority !== undefined && newRecord.Priority !== ''
          ? Number(newRecord.Priority)
          : null,
        RequestDate: newRecord.RequestDate ? new Date(newRecord.RequestDate).toISOString() : null,
        DueDate: newRecord.DueDate ? new Date(newRecord.DueDate).toISOString() : null,
        Comments: newRecord.Comments || null,
        SupportEngineer: newRecord.SupportEngineer || null,
        NonEmployeeRequest: newRecord.NonEmployeeRequest || null,
        Customer: newRecord.Customer || null,
        SendEmail: Boolean(newRecord.SendEmail),
        // Set other fields that the API expects but aren't in the form
        SalesPortalProjectID: null,
        SaleQuoteID: null,
        ProductionPortalProjectID: null,
        DateCompleted: null,
        KeyAccountRSM: null
      };

      const response = await dispatch(postData({
        url: activityLogUrl,
        data: apiData
      })).unwrap();

      // Log the creation of the new record (like schedule change log for new jobs)
      try {
        // Get the new record ID from response or refresh and find it
        let newRecordId = response?.ID;
        if (!newRecordId) {
          // If ID not in response, refresh data and find the newest record
          await fetchActivityLog();
          const refreshedData = await dispatch(fetchData({ url: activityLogUrl })).unwrap();
          const newestRecord = refreshedData.sort((a, b) => new Date(b.RequestDate) - new Date(a.RequestDate))[0];
          newRecordId = newestRecord?.ID;
        }

        if (newRecordId) {
          // Log creation for significant fields (like schedule change log pattern)
          const fieldsToLog = ['Market', 'Status', 'ProjectName', 'JobNumber', 'ProductCode', 'RequestType', 'RSM', 
                               'ProjectManager', 'Priority', 'RequestDate', 'DueDate', 'Comments', 
                               'SupportEngineer', 'NonEmployeeRequest', 'Customer'];
          
          for (const field of fieldsToLog) {
            if (newRecord[field]) {
              await logActivityLogChange(
                newRecordId,
                newRecord.ProjectName || '',
                newRecord.JobNumber || '',
                field,
                '',
                newRecord[field]
              );
            }
          }
        }
      } catch (logError) {
        console.warn('Record created successfully but change logging failed:', logError);
        // Don't throw here - the main creation worked
      }

      // Refresh the activity log to show the new record
      await fetchActivityLog();
      
      setShowNewRecordModal(false);
      showSaveNotificationMessage("New record added successfully!", "success");
    } catch (err) {
      console.error("Error saving new record:", err);
      showSaveNotificationMessage("Error adding new record. Please try again.", "error");
    }
  };

  // Copy from existing functionality
  const handleCopyFromExisting = () => {
    setShowCopySearch(true);
    setCopySearchTerm("");
    setCopySearchResults([]);
  };

  const handleCopySearch = (searchTerm) => {
    setCopySearchTerm(searchTerm);
    
    if (!searchTerm.trim()) {
      setCopySearchResults([]);
      return;
    }

    // Search through full unfiltered activity log data for matches by Project Name or Job Number
    const results = fullActivityLog.filter(item => {
      const projectNameMatch = item.ProjectName && 
        item.ProjectName.toLowerCase().includes(searchTerm.toLowerCase());
      const jobNumberMatch = item.JobNumber && 
        item.JobNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
      return projectNameMatch || jobNumberMatch;
    }).slice(0, 10); // Limit to 10 results

    setCopySearchResults(results);
  };

  const handleSelectExistingRecord = (selectedItem) => {
    // Auto-populate specified fields from the selected item
    setNewRecord(prev => ({
      ...prev,
      ProjectName: selectedItem.ProjectName || "",
      Market: selectedItem.Market || "",
      Status: selectedItem.Status || "",
      JobNumber: selectedItem.JobNumber || "",
      ProductCode: selectedItem.ProductCode || "",
      RSM: selectedItem.RSM || "",
      Customer: selectedItem.Customer || ""
    }));

    // Close the copy search interface
    setShowCopySearch(false);
    setCopySearchTerm("");
    setCopySearchResults([]);
  };

  const handleCancelCopySearch = () => {
    setShowCopySearch(false);
    setCopySearchTerm("");
    setCopySearchResults([]);
  };

  // Save current view configuration
  const saveView = async () => {
    if (!newViewName || !currentUser) return;

    const viewConfig = {
      viewType,
      visibleColumns,
      columnOrder,
      sortConfig,
      groupBy,
      filters,
      searchTerm,
      hideCompleted,
      showOnlyMyJobs,
      dateCompletedRange,
      keepAccordionsOpen,
      activeAccordionKeys,
      nestedActiveKeys,
      description: newViewDescription
    };

    try {
      await dispatch(postData({
        url: userPreferencesUrl,
        data: {
          userObjectID: currentUser,
          category: "AdvancedActivityLogViews",
          key: newViewName,
          value: JSON.stringify(viewConfig)
        }
      })).unwrap();

      setNewViewName("");
      setNewViewDescription("");
      setShowSaveViewModal(false);
      fetchSavedViews();
      showSaveNotificationMessage("View saved successfully!", "success");
    } catch (err) {
      console.error("Error saving view:", err);
      showSaveNotificationMessage("Error saving view. Please try again.", "error");
    }
  };

  // Load a saved view
  const loadView = (savedView) => {
    try {
      const config = JSON.parse(savedView.Value);
      
      // Set loading flag to prevent filters from being cleared
      setIsLoadingView(true);
      
      setViewType(config.viewType || "accordion");
      
      // Ensure status and actions are always first in visible columns
      const configVisibleColumns = config.visibleColumns || [];
      const otherColumns = configVisibleColumns.filter(col => !['status', 'actions'].includes(col));
      setVisibleColumns(['status', 'actions', ...otherColumns]);
      
      // Ensure status and actions are always first in column order
      const configColumnOrder = config.columnOrder || [];
      const otherOrderColumns = configColumnOrder.filter(col => !['status', 'actions'].includes(col));
      setColumnOrder(['status', 'actions', ...otherOrderColumns]);
      
      setSortConfig(config.sortConfig || { key: "DueDate", direction: "asc" });
      setGroupBy(config.groupBy || "Market");
      setFilters(config.filters || {});
      setSearchTerm(config.searchTerm || "");
      setHideCompleted(config.hideCompleted !== undefined ? config.hideCompleted : true);
      setShowOnlyMyJobs(config.showOnlyMyJobs !== undefined ? config.showOnlyMyJobs : true);
      
      // Load date completed range filter preferences
      setDateCompletedRange(config.dateCompletedRange || { enabled: false, daysBack: 30 });
      
      // Load accordion preferences
      setKeepAccordionsOpen(config.keepAccordionsOpen || false);
      setActiveAccordionKeys(config.activeAccordionKeys || ['0']);
      setNestedActiveKeys(config.nestedActiveKeys || {});
      
      // Clear loading flag after a brief delay to ensure all state updates have completed
      setTimeout(() => setIsLoadingView(false), 100);
      
      setShowLoadViewModal(false);
      showSaveNotificationMessage(`View "${savedView.Key}" loaded successfully!`, "success");
    } catch (err) {
      console.error("Error loading view:", err);
      showSaveNotificationMessage("Error loading view. Please try again.", "error");
      setIsLoadingView(false); // Clear loading flag on error
    }
  };

  // Delete a saved view
  const deleteView = async (viewId) => {
    try {
      await dispatch(deleteData({
        url: userPreferencesUrl,
        deleteId: { ID: viewId }
      })).unwrap();
      fetchSavedViews();
      showSaveNotificationMessage("View deleted successfully!", "success");
    } catch (err) {
      console.error("Error deleting view:", err);
      showSaveNotificationMessage("Error deleting view. Please try again.", "error");
    }
  };

  // Handle edit record
  const handleEditRecord = (record) => {
    setEditingRecord({ ...record });
    setShowEditModal(true);
  };

  // Handle save edited record
  const handleSaveEditedRecord = async (updatedRecord) => {
    if (!activityLogUrl) return;

    try {
      setSaveStatus("Saving changes...");
      setShowSaveBanner(true);

      // Convert form data to API format with PascalCase field names for PATCH
      const updateData = {
        ID: updatedRecord.ID,
        Market: updatedRecord.Market || null,
        Status: updatedRecord.Status || null,
        ProjectName: updatedRecord.ProjectName || null,
        JobNumber: updatedRecord.JobNumber || null,
        ProductCode: updatedRecord.ProductCode || null,
        RequestType: updatedRecord.RequestType || null,
        RSM: updatedRecord.RSM || null,
        ProjectManager: updatedRecord.ProjectManager || null,
        Priority: updatedRecord.Priority || null,
        RequestDate: updatedRecord.RequestDate ? new Date(updatedRecord.RequestDate).toISOString() : null,
        DueDate: updatedRecord.DueDate ? new Date(updatedRecord.DueDate).toISOString() : null,
        Comments: updatedRecord.Comments || null,
        SupportEngineer: updatedRecord.SupportEngineer || null,
        NonEmployeeRequest: updatedRecord.NonEmployeeRequest || null,
        Customer: updatedRecord.Customer || null,
        DateCompleted: updatedRecord.DateCompleted ? new Date(updatedRecord.DateCompleted).toISOString() : null,
        SendEmail: updatedRecord.SendEmail || false
      };

      // Use axiosInstance for PATCH request (like in cell editing)
      const updateResponse = await axiosInstance.patch(`${activityLogUrl}?id=${updatedRecord.ID}`, updateData);

      if (updateResponse.status >= 200 && updateResponse.status < 300) {
        // Get original record for change comparison and notifications
        const originalRecord = activityLog.find(log => log.ID === updatedRecord.ID);
        const dateCompletedChanged = !originalRecord?.DateCompleted && updatedRecord.DateCompleted;
        const projectManagerChanged = originalRecord?.ProjectManager !== updatedRecord.ProjectManager;
        
        // Update local state
        setActivityLog(prev => prev.map(log => 
          log.ID === updatedRecord.ID ? { ...log, ...updateData } : log
        ));

        // Detect and log all changes (like schedule change log)
        try {
          const fieldsToCheck = ['Market', 'Status', 'ProjectName', 'JobNumber', 'ProductCode', 'RequestType', 'RSM', 
                                 'ProjectManager', 'Priority', 'RequestDate', 'DueDate', 'Comments', 
                                 'SupportEngineer', 'NonEmployeeRequest', 'Customer', 'DateCompleted', 'SendEmail'];
          
          for (const field of fieldsToCheck) {
            const originalValue = originalRecord?.[field] || '';
            const newValue = updatedRecord[field] || '';
            
            // Convert values to strings for comparison to handle null/undefined/empty cases
            const originalStr = String(originalValue).trim();
            const newStr = String(newValue).trim();
            
            if (originalStr !== newStr) {
              await logActivityLogChange(
                updatedRecord.ID,
                updatedRecord.ProjectName || '',
                updatedRecord.JobNumber || '',
                field,
                originalValue,
                newValue
              );
            }
          }
        } catch (logError) {
          console.warn('Main update succeeded but change logging failed:', logError);
          // Don't throw here - the main update worked
        }

        // Send completion email if DateCompleted was newly set and SendEmail is true
        if (dateCompletedChanged && updatedRecord.SendEmail) {
          await sendCompletionEmail(updatedRecord);
        }

        // Send Project Manager change notification if ProjectManager was changed
        if (projectManagerChanged) {
          await sendProjectManagerChangeEmail(updatedRecord, originalRecord?.ProjectManager, updatedRecord.ProjectManager);
        }

        setShowEditModal(false);
        setEditingRecord(null);
        setSaveStatus("Record updated successfully!");
        setShowSaveBanner(true);
      } else {
        throw new Error(`Server responded with status ${updateResponse.status}`);
      }
    } catch (err) {
      console.error("Error updating record:", err);
      setSaveStatus(`Error updating record: ${err.message || 'Unknown error'}`);
      setShowSaveBanner(true);
    }
  };

  // Handle delete record
  const handleDeleteRecord = (record) => {
    setDeletingRecord(record);
    setShowDeleteConfirm(true);
  };

  // Confirm delete record
  const confirmDeleteRecord = async () => {
    if (!activityLogUrl || !deletingRecord) return;

    try {
      setSaveStatus("Deleting record...");
      setShowSaveBanner(true);

      await dispatch(deleteData({
        url: activityLogUrl,
        deleteId: { ID: deletingRecord.ID }
      })).unwrap();

      // Log the deletion (like schedule change log)
      try {
        await logActivityLogChange(
          deletingRecord.ID,
          deletingRecord.ProjectName || '',
          deletingRecord.JobNumber || '',
          'Record Deleted',
          'Active Record',
          'Deleted'
        );
      } catch (logError) {
        console.warn('Record deleted successfully but change logging failed:', logError);
        // Don't throw here - the main deletion worked
      }

      // Remove from local state
      setActivityLog(prev => prev.filter(log => log.ID !== deletingRecord.ID));

      // Close both the delete confirmation modal and the edit modal
      setShowDeleteConfirm(false);
      setDeletingRecord(null);
      setShowEditModal(false);
      setEditingRecord(null);
      
      setSaveStatus("Record deleted successfully!");
      setShowSaveBanner(true);
    } catch (err) {
      console.error("Error deleting record:", err);
      setSaveStatus(`Error deleting record: ${err.message || 'Unknown error'}`);
      setShowSaveBanner(true);
    }
  };

  // Helper function to get status background color
  const getStatusBackgroundColor = (item) => {
    if (item.DateCompleted) {
      return '#28a745'; // Green for completed
    }
    if (isOverdue(item.DueDate, item.DateCompleted)) {
      return '#dc3545'; // Red for overdue
    }
    return undefined; // Default/no color for pending
  };

  // Move column up in order
  const moveColumnUp = (columnKey) => {
    const currentIndex = columnOrder.indexOf(columnKey);
    // Prevent moving columns above status and actions (first two positions)
    if (currentIndex > 2) {
      const newOrder = [...columnOrder];
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = [newOrder[currentIndex], newOrder[currentIndex - 1]];
      setColumnOrder(newOrder);
    }
  };

  // Move column down in order
  const moveColumnDown = (columnKey) => {
    const currentIndex = columnOrder.indexOf(columnKey);
    // Allow normal movement down, but ensure status and actions stay in first two positions
    if (currentIndex < columnOrder.length - 1 && currentIndex >= 2) {
      const newOrder = [...columnOrder];
      [newOrder[currentIndex], newOrder[currentIndex + 1]] = [newOrder[currentIndex + 1], newOrder[currentIndex]];
      setColumnOrder(newOrder);
    }
  };

  // Drag & Drop state for column ordering
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const handleDragStart = (e, columnKey) => {
    e.dataTransfer.setData('text/plain', columnKey);
    // Use a transparent image to avoid default ghost if desired
  };

  const handleDragEnter = (e, columnKey) => {
    e.preventDefault();
    setDragOverColumn(columnKey);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = (e, columnKey) => {
    if (dragOverColumn === columnKey) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = e.dataTransfer.getData('text/plain');
    if (!draggedKey || draggedKey === targetKey) {
      setDragOverColumn(null);
      return;
    }
    // Preserve first two fixed columns
    const fixed = columnOrder.slice(0, 2);
    const rest = columnOrder.slice(2);
    if (!rest.includes(draggedKey) || !rest.includes(targetKey)) {
      setDragOverColumn(null);
      return;
    }
    const filtered = rest.filter(k => k !== draggedKey);
    const targetIndex = filtered.indexOf(targetKey);
    // Insert before target (simple model). Users can refine with buttons if needed.
    const newRest = [
      ...filtered.slice(0, targetIndex),
      draggedKey,
      ...filtered.slice(targetIndex)
    ];
    setColumnOrder([...fixed, ...newRest]);
    setDragOverColumn(null);
  };

  // Column visibility toggle
  const toggleColumnVisibility = (columnKey) => {
    // Prevent hiding status and actions columns
    if (['status', 'actions'].includes(columnKey)) {
      return;
    }
    
    setVisibleColumns(prev => {
      const isVisible = prev.includes(columnKey);
      if (isVisible) {
        // Removing visibility -> also remove from columnOrder (except keep original ordering for others)
        setColumnOrder(co => co.filter(c => c !== columnKey));
        return prev.filter(col => col !== columnKey);
      } else {
        // Adding visibility -> insert near end but before any non-selectable trailing columns if needed
        setColumnOrder(co => {
          if (co.includes(columnKey)) return co; // already present (unlikely if it was hidden)
          // Maintain first two fixed columns (status, actions)
            const fixed = co.slice(0,2);
            const rest = co.slice(2);
            return [...fixed, ...rest, columnKey];
        });
        return [...prev, columnKey];
      }
    });
  };

  // Cell editing functions
  const canEditCell = (item, field) => {
    // ProjectManager field can be edited by everyone
    if (field === 'ProjectManager') {
      return true;
    }
    // All other fields - only allow editing if current user is the project manager
    return item.ProjectManager === userDetails?.displayName || item.ProjectManager === userDetails?.name;
  };

  // Modal editing permissions - more restrictive
  const canEditRecord = (item) => {
    // Only allow editing if current user is the project manager
    return item.ProjectManager === userDetails?.displayName || item.ProjectManager === userDetails?.name;
  };

  const handleCellDoubleClick = (e, item, field) => {
    e.stopPropagation();
    
    if (!canEditCell(item, field)) {
      return;
    }

    // Don't allow editing of certain fields
    const nonEditableFields = ['ID', 'RequestDate'];
    if (nonEditableFields.includes(field)) {
      return;
    }

    setEditingCell({ itemId: item.ID, field });
    
    // Handle different field types for initial values
    let initialValue = item[field] || "";
    if ((field === 'DueDate' || field === 'DateCompleted') && initialValue) {
      // Convert date to YYYY-MM-DD format for date input using unified date utilities
      initialValue = formatDateForForm(initialValue);
    }
    
    setEditValue(initialValue);
    setOriginalValue(initialValue); // Store the original value
  };

  const handleCellKeyDown = (e, item, field) => {
    if (e.key === 'Enter') {
      handleSaveEdit(item, field);
    } else if (e.key === 'Escape') {
      setEditingCell({ itemId: null, field: null });
      setEditValue("");
      setOriginalValue("");
    }
  };

  const handleSaveEdit = async (item, field) => {
    // Only save if the value has actually changed
    if (editValue === originalValue) {
      // Show a brief "No changes" feedback
      setSaveStatus("No changes detected");
      setShowSaveBanner(true);
      
      setEditingCell({ itemId: null, field: null });
      setEditValue("");
      setOriginalValue("");
      return;
    }

    try {
      setSaveStatus("Saving...");
      setShowSaveBanner(true);

      // Prepare the update data with original field name capitalization (like JobSchedulePage)
      // Handle empty date values by converting them to null
      let fieldValue = editValue;
      const column = availableColumns.find(col => col.key === field);
      if (column?.type === "date" && (editValue === "" || editValue === null || editValue === undefined)) {
        fieldValue = null;
      }

      const updateData = {
        ID: item.ID,  // Keep ID capitalized
        [field]: fieldValue // Use processed field value
      };

      // Make API call using PATCH method with the ID in the URL (same pattern as JobSchedulePage)
      const updateResponse = await axiosInstance.patch(`${activityLogUrl}?id=${item.ID}`, updateData);

      // Only proceed with state update if the main API call succeeded
      if (updateResponse.status >= 200 && updateResponse.status < 300) {
        // Update local state
        const updatedItem = { ...item, [field]: fieldValue };
        setActivityLog(prev => prev.map(log => 
          log.ID === item.ID ? updatedItem : log
        ));

        // Log the change to activity log change log (like schedule change log)
        try {
          await logActivityLogChange(
            item.ID,
            item.ProjectName || '',
            item.JobNumber || '',
            field,
            originalValue,
            fieldValue
          );
        } catch (logError) {
          console.warn('Main update succeeded but change logging failed:', logError);
          // Don't throw here - the main update worked
        }

        // Send completion email if DateCompleted was set and SendEmail is true
        if (field === 'DateCompleted' && fieldValue && item.SendEmail) {
          await sendCompletionEmail(updatedItem);
        }

        // Send Project Manager change notification if ProjectManager was changed
        if (field === 'ProjectManager' && originalValue !== fieldValue) {
          await sendProjectManagerChangeEmail(updatedItem, originalValue, fieldValue);
        }

        setEditingCell({ itemId: null, field: null });
        setEditValue("");
        setOriginalValue("");
        
        setSaveStatus("Changes saved successfully!");
        setShowSaveBanner(true);
      } else {
        throw new Error(`Server responded with status ${updateResponse.status}`);
      }
    } catch (err) {
      console.error("Error saving edit:", err);
      setSaveStatus(`Error saving changes: ${err.message || 'Unknown error'}`);
      setShowSaveBanner(true);
      // Reset editing state on error
      setEditingCell({ itemId: null, field: null });
      setEditValue("");
      setOriginalValue("");
    }
  };

  const renderEditableCell = (item, field, value) => {
    const isEditing = editingCell.itemId === item.ID && editingCell.field === field;
    const canEdit = canEditCell(item, field);
    const nonEditableFields = ['ID', 'RequestDate'];
    const isFieldEditable = !nonEditableFields.includes(field);

    if (isEditing) {
      if (field === 'Comments') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  handleSaveEdit(item, field);
                } else if (e.key === 'Escape') {
                  setEditingCell({ itemId: null, field: null });
                  setEditValue("");
                  setOriginalValue("");
                }
              }}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '60px',
                border: 'none',
                padding: '8px',
                margin: 0,
                resize: 'vertical',
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0'
              }}
              placeholder="Ctrl+Enter to save, Esc to cancel"
            />
          </div>
        );
      } else if (field === 'DueDate' || field === 'DateCompleted') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <input
              type="date"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0'
              }}
            />
          </div>
        );
      } else if (field === 'Market') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Market</option>
              {marketOptionsForModal.map((option) => (
                <option key={option.ID} value={option.DisplayText}>
                  {option.DisplayText}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'Status') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Status</option>
              {activityLogStatusOptions.map((option, index) => (
                <option key={option.ID || index} value={option.DisplayText}>
                  {option.DisplayText}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'ProductCode') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Product Code</option>
              {productCodeOptions.map((option) => (
                <option key={option.ID} value={option.DisplayText}>
                  {option.DisplayText}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'RequestType') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Request Type</option>
              {requestTypeOptions.map((option) => (
                <option key={option.ID} value={option.DisplayText}>
                  {option.DisplayText}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'Priority') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Priority</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
          </div>
        );
      } else if (field === 'RSM') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select RSM</option>
              {rsmOptions.map((option) => (
                <option key={option.id || option.value} value={option.displayName || option.value}>
                  {option.displayName || option.value}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'ProjectManager') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Project Manager</option>
              {projectManagerOptions.map((option) => (
                <option key={option.id || option.value} value={option.displayName || option.value}>
                  {option.displayName || option.value}
                </option>
              ))}
            </select>
          </div>
        );
      } else if (field === 'SupportEngineer') {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <select
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0',
                backgroundColor: 'white'
              }}
            >
              <option value="">Select Support Engineer</option>
              {supportEngineerOptions.map((option) => (
                <option key={option.id || option.value} value={option.displayName || option.value}>
                  {option.displayName || option.value} ({option.group})
                </option>
              ))}
            </select>
          </div>
        );
      } else {
        return (
          <div style={{ width: '100%', height: '100%', padding: 0, margin: 0 }}>
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => handleCellKeyDown(e, item, field)}
              onBlur={() => handleSaveEdit(item, field)}
              autoFocus
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                padding: '8px',
                margin: 0,
                outline: 'none',
                boxShadow: '0 0 0 2px #0d6efd',
                borderRadius: '0'
              }}
              placeholder="Enter to save, Esc to cancel"
            />
          </div>
        );
      }
    }

    return (
      <div
        style={{
          cursor: canEdit && isFieldEditable ? 'pointer' : 'default',
          padding: '8px',
          margin: 0,
          backgroundColor: canEdit && isFieldEditable ? 'rgba(0, 123, 255, 0.05)' : 'transparent',
          border: canEdit && isFieldEditable ? '1px dashed rgba(0, 123, 255, 0.2)' : 'none',
          minHeight: '20px',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center'
        }}
        onDoubleClick={(e) => handleCellDoubleClick(e, item, field)}
        title={canEdit && isFieldEditable ? 'Double-click to edit (you are the Project Manager)' : ''}
      >
        {value || 'N/A'}
      </div>
    );
  };

  // Render table header with sorting
  const renderTableHeader = () => (
    <thead>
      <tr>
        {columnOrder.filter(col => visibleColumns.includes(col)).map(columnKey => {
          const column = availableColumns.find(col => col.key === columnKey);
          return (
            <th 
              key={columnKey} 
              onClick={() => handleSort(columnKey)}
              style={{ cursor: "pointer" }}
              data-field={columnKey}
            >
              {column?.label}
              {sortConfig.key === columnKey && (
                <span className="ms-1">
                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                </span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  // Render accordion with table rows inside
  const renderAccordionWithTable = (items) => (
    <div className="table-responsive">
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            {columnOrder
              .filter(col => visibleColumns.includes(col))
              .map(columnKey => {
                const column = availableColumns.find(col => col.key === columnKey);
                return (
                  <th key={columnKey} data-field={columnKey}>
                    {column?.label}
                  </th>
                );
              })}
          </tr>
        </thead>
        <tbody>
          {items.map(item => (
            <tr key={item.ID} className={isOverdue(item.DueDate, item.DateCompleted) ? "overdue-item" : ""}>
              {columnOrder
                .filter(col => visibleColumns.includes(col))
                .map(columnKey => {
                  const column = availableColumns.find(col => col.key === columnKey);
                  let value = item[columnKey];
                  
                  if (column?.type === "date") {
                    value = formatDate(value);
                  } else if (column?.type === "boolean") {
                    value = value ? "Yes" : "No";
                  }
                  
                  return (
                    <td 
                      key={columnKey}
                      data-field={columnKey}
                      data-label={column?.label || columnKey}
                      className={
                        editingCell.itemId === item.ID && editingCell.field === columnKey 
                          ? 'editing-cell' 
                          : (canEditCell(item, columnKey) && !['ID', 'RequestDate', 'status', 'actions'].includes(columnKey) 
                              ? 'editable-cell' 
                              : '')
                      }
                      style={{
                        width: column?.width || 'auto',
                        minWidth: column?.width || 'auto',
                        backgroundColor: columnKey === 'status' ? getStatusBackgroundColor(item) : undefined
                      }}
                    >
                      {columnKey === 'status' ? (
                        // Empty content for status column - background color indicates status
                        <span></span>
                      ) : columnKey === 'actions' ? (
                        // Only show edit button if current user is the project manager
                        canEditRecord(item) ? (
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => handleEditRecord(item)}
                            style={{ padding: '2px 6px' }}
                            title="Edit Record"
                          >
                            <i className="bi bi-pencil" style={{ fontSize: '12px' }}></i>
                          </Button>
                        ) : null
                      ) : (
                        renderEditableCell(item, columnKey, value)
                      )}
                    </td>
                  );
                })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const total = sortedData.length;
    const completed = sortedData.filter(item => item.DateCompleted).length;
    const overdue = sortedData.filter(item => isOverdue(item.DueDate, item.DateCompleted)).length;
    const pending = total - completed;

    return { total, completed, overdue, pending };
  }, [sortedData]);

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Loading activity log...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-3">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      {/* Activity Log Table Container with enhanced styling */}
      <div className="activity-log-table-container enhanced-table-container">
        <div className="table-wrapper enhanced-table-wrapper">
          {/* Controls section */}
          <Container fluid className="mb-3">
            <Accordion
              className="activity-log-controls-accordion"
              activeKey={controlsPanelOpen ? "filters-actions" : null}
              onSelect={(eventKey) => setControlsPanelOpen(eventKey === "filters-actions")}
            >
              <Accordion.Item eventKey="filters-actions">
                <Accordion.Header>Filters &amp; Actions</Accordion.Header>
                <Accordion.Body>
                  <Card className="activity-log-controls-card">
                    <Card.Body>
                <Row className="align-items-center activity-log-primary-controls">
                  <Col md={3} className="activity-log-control-field activity-log-search-field">
                    <Form.Group>
                      <Form.Label>Search</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Search all fields..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2} className="activity-log-control-field activity-log-view-type-field">
                    <Form.Group>
                      <Form.Label>View Type</Form.Label>
                      <Form.Select
                        value={viewType}
                        onChange={(e) => setViewType(e.target.value)}
                      >
                        <option value="table">Table</option>
                        <option value="accordion">Accordion</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="activity-log-control-field activity-log-group-by-field">
                    <Form.Group>
                      <Form.Label>Group By</Form.Label>
                      <Form.Select
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value)}
                      >
                        {groupingOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3} className="activity-log-actions">
                    <Form.Label>Actions</Form.Label>
                    <div className="activity-log-action-buttons">
                      <Button variant="success" size="sm" className="me-2" onClick={handleAddNewRecord}>
                        <i className="fas fa-plus me-1"></i>Add New Record
                      </Button>
                      <ButtonGroup className="me-2">
                        <Button variant="outline-primary" onClick={() => setShowColumnModal(true)}>
                          Columns
                        </Button>
                        <Button variant="outline-success" onClick={() => setShowSaveViewModal(true)}>
                          Save View
                        </Button>
                        <Button variant="outline-info" onClick={() => setShowLoadViewModal(true)}>
                          Load View
                        </Button>
                      </ButtonGroup>
                      <Button variant="outline-warning" onClick={clearFilters}>
                        Clear Filters
                      </Button>
                    </div>
                  </Col>
                  <Col md={2} className="activity-log-quick-options">
                    <Form.Group>
                      <Form.Label>Quick Options</Form.Label>
                      <div>
                        <Form.Check
                          type="checkbox"
                          id="hideCompleted"
                          label="Hide Completed"
                          checked={hideCompleted}
                          onChange={(e) => setHideCompleted(e.target.checked)}
                          className="mb-1"
                        />
                        <Form.Check
                          type="checkbox"
                          id="showOnlyMyJobs"
                          label="Show Only My Jobs"
                          checked={showOnlyMyJobs}
                          onChange={(e) => setShowOnlyMyJobs(e.target.checked)}
                          className="mb-1"
                        />
                        <Form.Check
                          type="checkbox"
                          id="keepAccordionsOpen"
                          label="Keep Accordions Open"
                          checked={keepAccordionsOpen}
                          onChange={(e) => setKeepAccordionsOpen(e.target.checked)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Quick Filters */}
                <Row className="mt-3 activity-log-quick-filters">
                  {["Market", "RequestType", "ProductCode", "ProjectManager", "RSM", "Status"].map(filterKey => (
                    <Col md={2} key={filterKey}>
                      <Form.Group>
                        <Form.Label>{filterKey === "ProjectManager" ? "Project Manager" : filterKey}</Form.Label>
                        <Form.Select
                          value={filters[filterKey] || ""}
                          onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                        >
                          <option value="">All</option>
                          {getFilteredUniqueValues(filterKey).map(value => (
                            <option key={value} value={value}>{value}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
                
                {/* Additional Filters Row - Date Completed Range Filter */}
                <Row className="mt-2 activity-log-date-filter-row">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Date Completed Filter</Form.Label>
                      <div className="date-completed-range-filter">
                        <Form.Check
                          type="checkbox"
                          id="enableDateCompletedRange"
                          label="Show completed jobs from last X days + all uncompleted"
                          checked={dateCompletedRange.enabled}
                          onChange={(e) => setDateCompletedRange(prev => ({ ...prev, enabled: e.target.checked }))}
                        />
                        {dateCompletedRange.enabled && (
                          <>
                            <Form.Range
                              min="1"
                              max="365"
                              value={dateCompletedRange.daysBack}
                              onChange={(e) => setDateCompletedRange(prev => ({ ...prev, daysBack: parseInt(e.target.value) }))}
                              style={{ width: '150px' }}
                            />
                            <span className="text-nowrap">{dateCompletedRange.daysBack} days</span>
                          </>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                    </Card.Body>
                  </Card>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Container>

          {/* Table Header with item count */}
          <Container fluid>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5>
                  Advanced Activity Log ({sortedData.length} items)
                  {dateCompletedRange.enabled && (
                    <Badge bg="info" className="ms-2">
                      Showing completed jobs from last {dateCompletedRange.daysBack} days + all uncompleted jobs
                    </Badge>
                  )}
                  {keepAccordionsOpen && (
                    <Badge bg="success" className="ms-2">
                      All Accordions Open
                    </Badge>
                  )}
                </h5>
              </Card.Header>
        <Card.Body>
          {groupBy === "none" ? (
            // No grouping - show all items based on view type
            <div>
              {viewType === "table" ? (
                // Table view - all items in one table
                <div className="table-responsive">
                  <Table striped bordered hover>
                    {renderTableHeader()}
                    <tbody>
                      {Object.entries(groupedData).map(([groupName, projectGroups]) => (
                        <React.Fragment key={groupName}>
                          {Object.entries(projectGroups).map(([projectName, items]) => 
                            items.map(item => (
                              <tr key={item.ID} className={isOverdue(item.DueDate, item.DateCompleted) ? "overdue-item" : ""}>
                                {columnOrder.filter(col => visibleColumns.includes(col)).map(columnKey => {
                                  const column = availableColumns.find(col => col.key === columnKey);
                                  let value = item[columnKey];
                                  
                                  if (column?.type === "date") {
                                    value = formatDate(value);
                                  } else if (column?.type === "boolean") {
                                    value = value ? "Yes" : "No";
                                  }
                                  
                                  return (
                                    <td 
                                      key={columnKey}
                                      data-field={columnKey}
                                      data-label={column?.label || columnKey}
                                      className={
                                        editingCell.itemId === item.ID && editingCell.field === columnKey 
                                          ? 'editing-cell' 
                                          : (canEditCell(item, columnKey) && !['ID', 'RequestDate', 'status', 'actions'].includes(columnKey) 
                                              ? 'editable-cell' 
                                              : '')
                                      }
                                      style={{
                                        width: column?.width || 'auto',
                                        minWidth: column?.width || 'auto',
                                        backgroundColor: columnKey === 'status' ? getStatusBackgroundColor(item) : undefined
                                      }}
                                    >
                                      {columnKey === 'status' ? (
                                        // Empty content for status column - background color indicates status
                                        <span></span>
                                      ) : columnKey === 'actions' ? (
                                        // Allow edit button for everyone since ProjectManager field is now editable by all
                                        <Button
                                          variant="link"
                                          size="sm"
                                          onClick={() => handleEditRecord(item)}
                                          style={{ padding: '2px 6px' }}
                                          title="Edit Record"
                                        >
                                          <i className="bi bi-pencil" style={{ fontSize: '12px' }}></i>
                                        </Button>
                                      ) : (
                                        renderEditableCell(item, columnKey, value)
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                // Accordion view - grouped by project
                Object.entries(groupedData).map(([groupName, projectGroups]) => (
                  <div key={groupName}>
                    <div style={{ marginLeft: '15px', paddingLeft: '10px', borderLeft: '2px solid #e9ecef' }}>
                      <Accordion 
                        activeKey={getAccordionActiveKey(
                          nestedActiveKeys[groupName] || (keepAccordionsOpen
                            ? Object.keys(projectGroups).map((_, index) => index.toString())
                            : []),
                          keepAccordionsOpen
                        )}
                        onSelect={(keys) => {
                          setNestedActiveKeys(prev => ({
                            ...prev,
                            [groupName]: normalizeAccordionSelection(keys)
                          }));
                        }}
                        alwaysOpen={keepAccordionsOpen}
                      >
                        {Object.entries(projectGroups).map(([projectName, items], projectIndex) => (
                          <Accordion.Item key={projectName} eventKey={projectIndex.toString()}>
                            <Accordion.Header>
                              <strong>{projectName}</strong>
                              <Badge bg="secondary" className="ms-2">
                                {items.length} items
                              </Badge>
                            </Accordion.Header>
                            <Accordion.Body>
                              {renderAccordionWithTable(items)}
                            </Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            // Grouped view - Level 1 is always accordion, Level 2 uses selected view type
            <Accordion 
              activeKey={getAccordionActiveKey(activeAccordionKeys, keepAccordionsOpen)}
              onSelect={(keys) => setActiveAccordionKeys(normalizeAccordionSelection(keys))}
              alwaysOpen={keepAccordionsOpen}
            >
              {Object.entries(groupedData).map(([groupName, projectGroups], groupIndex) => (
                <Accordion.Item key={groupName} eventKey={groupIndex.toString()}>
                  <Accordion.Header>
                    <strong>{groupName}</strong> 
                    <Badge bg="secondary" className="ms-2">
                      {Object.values(projectGroups).reduce((total, items) => total + items.length, 0)} items
                    </Badge>
                  </Accordion.Header>
                  <Accordion.Body>
                    {viewType === "table" ? (
                      // Table view - all items within the group in one table
                      <div className="table-responsive">
                        <Table striped bordered hover size="sm">
                          <thead>
                            <tr>
                              {columnOrder
                                .filter(col => visibleColumns.includes(col))
                                .map(columnKey => {
                                  const column = availableColumns.find(col => col.key === columnKey);
                                  return (
                                    <th key={columnKey} data-field={columnKey}>
                                      {column?.label}
                                    </th>
                                  );
                                })}
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(projectGroups).map(([projectName, items]) => 
                              items.map(item => (
                                <tr key={item.ID} className={isOverdue(item.DueDate, item.DateCompleted) ? "overdue-item" : ""}>
                                  {columnOrder
                                    .filter(col => visibleColumns.includes(col))
                                    .map(columnKey => {
                                      const column = availableColumns.find(col => col.key === columnKey);
                                      let value = item[columnKey];
                                      
                                      if (column?.type === "date") {
                                        value = formatDate(value);
                                      } else if (column?.type === "boolean") {
                                        value = value ? "Yes" : "No";
                                      }
                                      
                                      return (
                                        <td 
                                          key={columnKey}
                                          data-field={columnKey}
                                          data-label={column?.label || columnKey}
                                          className={
                                            editingCell.itemId === item.ID && editingCell.field === columnKey 
                                              ? 'editing-cell' 
                                              : (canEditCell(item, columnKey) && !['ID', 'RequestDate', 'status', 'actions'].includes(columnKey) 
                                                  ? 'editable-cell' 
                                                  : '')
                                          }
                                          style={{
                                            width: column?.width || 'auto',
                                            minWidth: column?.width || 'auto',
                                            backgroundColor: columnKey === 'status' ? getStatusBackgroundColor(item) : undefined
                                          }}
                                        >
                                          {columnKey === 'status' ? (
                                            // Empty content for status column - background color indicates status
                                            <span></span>
                                          ) : columnKey === 'actions' ? (
                                            // Only show edit button if current user is the project manager
                                            canEditRecord(item) ? (
                                              <Button
                                                variant="link"
                                                size="sm"
                                                onClick={() => handleEditRecord(item)}
                                                style={{ padding: '2px 6px' }}
                                                title="Edit Record"
                                              >
                                                <i className="bi bi-pencil" style={{ fontSize: '12px' }}></i>
                                              </Button>
                                            ) : null
                                          ) : (
                                            renderEditableCell(item, columnKey, value)
                                          )}
                                        </td>
                                      );
                                    })}
                                </tr>
                              ))
                            )}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      // Accordion view - grouped by project within the group
                      <div style={{ marginLeft: '20px', paddingLeft: '15px', borderLeft: '2px solid #e9ecef' }}>
                        <Accordion 
                          activeKey={getAccordionActiveKey(
                            nestedActiveKeys[groupIndex.toString()] || (keepAccordionsOpen
                              ? Object.keys(projectGroups).map((_, index) => index.toString())
                              : []),
                            keepAccordionsOpen
                          )}
                          onSelect={(keys) => {
                            setNestedActiveKeys(prev => ({
                              ...prev,
                              [groupIndex.toString()]: normalizeAccordionSelection(keys)
                            }));
                          }}
                          alwaysOpen={keepAccordionsOpen}
                        >
                          {Object.entries(projectGroups).map(([projectName, items], projectIndex) => (
                            <Accordion.Item key={projectName} eventKey={projectIndex.toString()}>
                              <Accordion.Header>
                                <strong>{projectName}</strong>
                                <Badge bg="secondary" className="ms-2">
                                  {items.length} items
                                </Badge>
                              </Accordion.Header>
                              <Accordion.Body>
                                {renderAccordionWithTable(items)}
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      </div>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          )}
        </Card.Body>

        {/* Summary Stats */}
        <Card.Footer className="activity-log-summary">
          <h6>Summary Statistics</h6>
          <Row>
            <Col md={3}>
              <div className="text-center">
                <Badge bg="primary" className="fs-6">{summaryStats.total}</Badge>
                <div>Total Items</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <Badge bg="success" className="fs-6">{summaryStats.completed}</Badge>
                <div>Completed</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <Badge bg="warning" className="fs-6">{summaryStats.pending}</Badge>
                <div>Pending</div>
              </div>
            </Col>
            <Col md={3}>
              <div className="text-center">
                <Badge bg="danger" className="fs-6">{summaryStats.overdue}</Badge>
                <div>Overdue</div>
              </div>
            </Col>
          </Row>
        </Card.Footer>
      </Card>
      </Container>

      {/* Column Configuration Modal */}
      <Modal show={showColumnModal} onHide={() => setShowColumnModal(false)} size="lg" dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Configure Columns</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={6}>
              <h6>Column Visibility</h6>
              {availableColumns
                .filter(column => !['status', 'actions', 'ID', 'SendEmail'].includes(column.key))
                .map(column => (
                <div key={column.key} className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label={column.label}
                    checked={visibleColumns.includes(column.key)}
                    onChange={() => toggleColumnVisibility(column.key)}
                  />
                </div>
              ))}
            </Col>
            <Col md={6}>
              <h6>Column Order</h6>
              <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                {columnOrder
                  // Show only selectable columns that are also currently visible
                  .filter(columnKey => !['status', 'actions', 'ID', 'SendEmail'].includes(columnKey) && visibleColumns.includes(columnKey))
                  .map((columnKey, index, filteredArray) => {
                  const column = availableColumns.find(col => col.key === columnKey);
                  if (!column) return null;
                  
                  return (
                    <div
                      key={columnKey}
                      className={`d-flex align-items-center mb-2 p-2 border rounded ${dragOverColumn === columnKey ? 'bg-light' : ''}`}
                      style={{ cursor: 'grab', opacity: dragOverColumn === columnKey ? 0.85 : 1 }}
                      draggable
                      onDragStart={(e) => handleDragStart(e, columnKey)}
                      onDragEnter={(e) => handleDragEnter(e, columnKey)}
                      onDragOver={handleDragOver}
                      onDragLeave={(e) => handleDragLeave(e, columnKey)}
                      onDrop={(e) => handleDrop(e, columnKey)}
                    >
                      <span className="flex-grow-1"><i className="fas fa-grip-vertical me-2 text-muted" />{column.label}</span>
                      <ButtonGroup size="sm">
                        <Button 
                          variant="outline-primary" 
                          onClick={() => moveColumnUp(columnKey)}
                          disabled={index === 0}
                          title="Move Up"
                        >
                          ↑
                        </Button>
                        <Button 
                          variant="outline-primary" 
                          onClick={() => moveColumnDown(columnKey)}
                          disabled={index === filteredArray.length - 1}
                          title="Move Down"
                        >
                          ↓
                        </Button>
                      </ButtonGroup>
                    </div>
                  );
                })}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowColumnModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Save View Modal */}
      <Modal show={showSaveViewModal} onHide={() => setShowSaveViewModal(false)} dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Save Current View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>View Name *</Form.Label>
            <Form.Control
              type="text"
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              placeholder="Enter view name..."
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newViewDescription}
              onChange={(e) => setNewViewDescription(e.target.value)}
              placeholder="Describe this view configuration..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveViewModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveView} disabled={!newViewName}>
            Save View
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Load View Modal */}
      <Modal show={showLoadViewModal} onHide={() => setShowLoadViewModal(false)} size="lg" dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Load Saved View</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {savedViews.length === 0 ? (
            <Alert variant="info">
              <Alert.Heading>No Saved Views</Alert.Heading>
              <p>You haven't saved any custom views yet. Use the "Save View" button to save your current configuration.</p>
            </Alert>
          ) : (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {savedViews.map(view => {
                let description = "";
                try {
                  const config = JSON.parse(view.Value);
                  description = config.description || "No description provided";
                } catch (err) {
                  description = "Unable to parse view configuration";
                }
                
                return (
                  <Card key={view.ID} className="mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <Card.Title className="h6 mb-2">{view.Key}</Card.Title>
                          <Card.Text className="text-muted small mb-0">
                            {description}
                          </Card.Text>
                        </div>
                        <ButtonGroup size="sm">
                          <Button variant="outline-primary" onClick={() => loadView(view)}>
                            Load
                          </Button>
                          <Button 
                            variant="outline-danger" 
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete the view "${view.Key}"?`)) {
                                deleteView(view.ID);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </ButtonGroup>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLoadViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New Record Modal */}
      <Modal show={showNewRecordModal} onHide={() => setShowNewRecordModal(false)} size="lg" dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Add New Activity Log Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Copy from Existing Section */}
            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-primary">
                  <Card.Header className="bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                      <span><i className="fas fa-copy me-2"></i>Copy from Existing Record</span>
                      {!showCopySearch && (
                        <Button 
                          variant="light" 
                          size="sm" 
                          onClick={handleCopyFromExisting}
                        >
                          <i className="fas fa-search me-1"></i>Search & Copy
                        </Button>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    {!showCopySearch ? (
                      <div></div>
                    ) : (
                      <div>
                        <Row className="mb-3">
                          <Col md={8}>
                            <Form.Group>
                              <Form.Label>Search by Project Name or Job Number</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter project name or job number..."
                                value={copySearchTerm}
                                onChange={(e) => handleCopySearch(e.target.value)}
                                autoFocus
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4} className="d-flex align-items-end">
                            <Button 
                              variant="secondary" 
                              onClick={handleCancelCopySearch}
                              className="mb-3"
                            >
                              Cancel Search
                            </Button>
                          </Col>
                        </Row>
                        
                        {copySearchResults.length > 0 && (
                          <div>
                            <Form.Label>Select a record to copy from:</Form.Label>
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                              {copySearchResults.map((item, index) => (
                                <Card 
                                  key={item.ID || index} 
                                  className="mb-2 copy-from-existing-card border-hover"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleSelectExistingRecord(item)}
                                >
                                  <Card.Body className="py-2">
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                          <strong>{item.ProjectName || 'No Project Name'}</strong>
                                          <small className="text-muted">
                                            {formatDateForTable(item.RequestDate) || 'No Date'}
                                          </small>
                                        </div>
                                        <div className="text-muted small">
                                          Job: {item.JobNumber || 'N/A'} | Market: {item.Market || 'N/A'} | Product: {item.ProductCode || 'N/A'}
                                        </div>
                                        <div className="text-muted small">
                                          RSM: {item.RSM || 'N/A'} | Customer: {item.Customer || 'N/A'}
                                        </div>
                                      </div>
                                      <i className="fas fa-chevron-right text-primary ms-2" style={{ marginTop: '2px' }}></i>
                                    </div>
                                  </Card.Body>
                                </Card>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {copySearchTerm && copySearchResults.length === 0 && (
                          <Alert variant="info">
                            No records found matching "{copySearchTerm}". Try a different search term.
                          </Alert>
                        )}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Market</Form.Label>
                  <Form.Select
                    value={newRecord.Market}
                    onChange={(e) => handleNewRecordChange('Market', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.Market || newRecord.Market.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Market</option>
                    {marketOptionsForModal.map((option, index) => (
                      <option key={option.ID || index} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={newRecord.Status}
                    onChange={(e) => handleNewRecordChange('Status', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.Status || newRecord.Status.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Status</option>
                    {activityLogStatusOptions.map((option, index) => (
                      <option key={option.ID || index} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRecord.ProjectName}
                    onChange={(e) => handleNewRecordChange('ProjectName', e.target.value)}
                    placeholder="Enter Project Name"
                    style={{
                      backgroundColor: !newRecord.ProjectName || newRecord.ProjectName.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRecord.JobNumber}
                    onChange={(e) => handleNewRecordChange('JobNumber', e.target.value)}
                    placeholder="Enter Job Number"
                    style={{
                      backgroundColor: !newRecord.JobNumber || newRecord.JobNumber.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Product Code</Form.Label>
                  <Form.Select
                    value={newRecord.ProductCode}
                    onChange={(e) => handleNewRecordChange('ProductCode', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.ProductCode || newRecord.ProductCode.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Product Code</option>
                    {productCodeOptions.map(option => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Request Type</Form.Label>
                  <Form.Select
                    value={newRecord.RequestType}
                    onChange={(e) => handleNewRecordChange('RequestType', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.RequestType || newRecord.RequestType.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Request Type</option>
                    {requestTypeOptions.map(option => (
                      <option key={option.ID} value={option.DisplayText}>
                        {option.DisplayText}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>RSM</Form.Label>
                  <Form.Select
                    value={newRecord.RSM}
                    onChange={(e) => handleNewRecordChange('RSM', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.RSM || newRecord.RSM.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select RSM</option>
                    {rsmOptions.map((option, index) => (
                      <option key={option.displayName || index} value={option.displayName}>
                        {option.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    value={newRecord.Priority}
                    onChange={(e) => handleNewRecordChange('Priority', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.Priority || newRecord.Priority.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Priority</option>
                    <option value="1">1</option>
                    <option value="2">2</option>
                    <option value="3">3</option>
                    <option value="4">4</option>
                    <option value="5">5</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Manager</Form.Label>
                  <Form.Select
                    value={newRecord.ProjectManager}
                    onChange={(e) => handleNewRecordChange('ProjectManager', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.ProjectManager || newRecord.ProjectManager.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Project Manager</option>
                    {projectManagerOptions.map((option, index) => (
                      <option key={option.displayName || index} value={option.displayName}>
                        {option.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Support Engineer</Form.Label>
                  <Form.Select
                    value={newRecord.SupportEngineer}
                    onChange={(e) => handleNewRecordChange('SupportEngineer', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.SupportEngineer || newRecord.SupportEngineer.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  >
                    <option value="">Select Support Engineer</option>
                    {supportEngineerOptions.map((option, index) => (
                      <option key={option.displayName || index} value={option.displayName}>
                        {option.displayName} ({option.group})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Request Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newRecord.RequestDate}
                    onChange={(e) => handleNewRecordChange('RequestDate', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.RequestDate || newRecord.RequestDate.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newRecord.DueDate}
                    onChange={(e) => handleNewRecordChange('DueDate', e.target.value)}
                    style={{
                      backgroundColor: !newRecord.DueDate || newRecord.DueDate.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Customer</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRecord.Customer}
                    onChange={(e) => handleNewRecordChange('Customer', e.target.value)}
                    placeholder="Enter Customer"
                    style={{
                      backgroundColor: !newRecord.Customer || newRecord.Customer.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Non-Employee Request</Form.Label>
                  <Form.Control
                    type="text"
                    value={newRecord.NonEmployeeRequest}
                    onChange={(e) => handleNewRecordChange('NonEmployeeRequest', e.target.value)}
                    placeholder="Enter Non-Employee Request"
                    style={{
                      backgroundColor: !newRecord.NonEmployeeRequest || newRecord.NonEmployeeRequest.trim() === '' ? '#fffbf0' : 'white'
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Comments</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={newRecord.Comments}
                    onChange={(e) => handleNewRecordChange('Comments', e.target.value)}
                    placeholder="Enter Comments"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="sendEmail"
                    label="Send Email Notification"
                    checked={newRecord.SendEmail}
                    onChange={(e) => handleNewRecordChange('SendEmail', e.target.checked)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowNewRecordModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveNewRecord}>
            Save Record
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Record Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Edit Activity Log Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingRecord && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Market</Form.Label>
                    <Form.Select
                      value={editingRecord.Market || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, Market: e.target.value }))}
                    >
                      <option value="">Select Market</option>
                      {marketOptionsForModal.map((option, index) => (
                        <option key={option.ID || index} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editingRecord.Status || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, Status: e.target.value }))}
                    >
                      <option value="">Select Status</option>
                      {activityLogStatusOptions.map((option, index) => (
                        <option key={option.ID || index} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingRecord.ProjectName || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, ProjectName: e.target.value }))}
                      placeholder="Enter Project Name"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Job Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingRecord.JobNumber || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, JobNumber: e.target.value }))}
                      placeholder="Enter Job Number"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Product Code</Form.Label>
                    <Form.Select
                      value={editingRecord.ProductCode || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, ProductCode: e.target.value }))}
                    >
                      <option value="">Select Product Code</option>
                      {productCodeOptions.map(option => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Type</Form.Label>
                    <Form.Select
                      value={editingRecord.RequestType || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, RequestType: e.target.value }))}
                    >
                      <option value="">Select Request Type</option>
                      {requestTypeOptions.map(option => (
                        <option key={option.ID} value={option.DisplayText}>
                          {option.DisplayText}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>RSM</Form.Label>
                    <Form.Select
                      value={editingRecord.RSM || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, RSM: e.target.value }))}
                    >
                      <option value="">Select RSM</option>
                      {rsmOptions.map((option, index) => (
                        <option key={option.displayName || index} value={option.displayName}>
                          {option.displayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority</Form.Label>
                    <Form.Select
                      value={editingRecord.Priority || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, Priority: e.target.value }))}
                    >
                      <option value="">Select Priority</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Project Manager</Form.Label>
                    <Form.Select
                      value={editingRecord.ProjectManager || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, ProjectManager: e.target.value }))}
                    >
                      <option value="">Select Project Manager</option>
                      {projectManagerOptions.map((option, index) => (
                        <option key={option.displayName || index} value={option.displayName}>
                          {option.displayName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Support Engineer</Form.Label>
                    <Form.Select
                      value={editingRecord.SupportEngineer || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, SupportEngineer: e.target.value }))}
                    >
                      <option value="">Select Support Engineer</option>
                      {supportEngineerOptions.map((option, index) => (
                        <option key={option.displayName || index} value={option.displayName}>
                          {option.displayName} ({option.group})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formatDateForForm(editingRecord.RequestDate)}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, RequestDate: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formatDateForForm(editingRecord.DueDate)}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, DueDate: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Date Completed</Form.Label>
                    <Form.Control
                      type="date"
                      value={formatDateForForm(editingRecord.DateCompleted)}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, DateCompleted: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Customer</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingRecord.Customer || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, Customer: e.target.value }))}
                      placeholder="Enter Customer"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Non-Employee Request</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingRecord.NonEmployeeRequest || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, NonEmployeeRequest: e.target.value }))}
                      placeholder="Enter Non-Employee Request"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Comments</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingRecord.Comments || ""}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, Comments: e.target.value }))}
                      placeholder="Enter Comments"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      id="editSendEmail"
                      label="Send Email Notification"
                      checked={editingRecord.SendEmail || false}
                      onChange={(e) => setEditingRecord(prev => ({ ...prev, SendEmail: e.target.checked }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => handleDeleteRecord(editingRecord)}>
            <i className="fas fa-trash me-1"></i>Delete Record
          </Button>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => handleSaveEditedRecord(editingRecord)}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            <Alert.Heading>Are you sure?</Alert.Heading>
            <p>
              This will permanently delete the activity log record for:
            </p>
            <strong>{deletingRecord?.ProjectName || 'Unknown Project'}</strong>
            <p className="mt-2 mb-0">
              This action cannot be undone.
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteRecord}>
            <i className="fas fa-trash me-1"></i>Delete Record
          </Button>
        </Modal.Footer>
      </Modal>
        </div>
      </div>
    </>
  );
};

AdvancedActivityLog.propTypes = {
  showSaveNotificationMessage: PropTypes.func.isRequired,
};

export default AdvancedActivityLog;
